import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { useDomainStore } from '@/hooks/useStores';
import { familiesApi, type FamilyUserResponse } from '@/api/familiesWrapper';
import {
  canChangeRoles,
  canChangeUserRole,
  getAssignableRoles,
  getRoleDisplayName,
  type UserRole
} from '@/utils/rolePermissions';
import Spinner from '@/components/shared/Spinner';
import '@/styles/form-select.css';
import './collaborators-role-list.css';

interface CollaboratorsRoleListProps {
  familyId: string;
}

export const CollaboratorsRoleList: React.FC<CollaboratorsRoleListProps> = observer(({ familyId }) => {
  const { t } = useTranslation('forms');
  const domainStore = useDomainStore();
  const actorRole = domainStore.currentFamily?.userRole;
  const plan = domainStore.currentFamily?.plan;
  const actorUserId = domainStore.user?.id;

  const [users, setUsers] = useState<FamilyUserResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canManage = canChangeRoles(actorRole, plan);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await familiesApi.getFamilyUsers(familyId);
      if (result.success) {
        setUsers(result.data.users.filter((u) => u.status === 'active'));
      } else {
        setError(result.error.getUserMessage() || t('collaborators.errors.loadFailed'));
        setUsers([]);
      }
    } catch {
      setError(t('collaborators.errors.loadFailed'));
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [familyId, t]);

  useEffect(() => {
    if (canManage) {
      loadUsers();
    }
  }, [canManage, loadUsers]);

  if (!canManage) {
    return null;
  }

  const handleRoleChange = async (userId: string, role: string) => {
    setActionError(null);
    setUpdatingUserId(userId);
    try {
      const updated = await domainStore.updateFamilyUserRole(familyId, userId, role);
      setUsers((prev) => prev.map((u) => (u.userId === userId ? { ...u, role: updated.role } : u)));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('collaborators.errors.updateRoleFailed'));
    } finally {
      setUpdatingUserId(null);
    }
  };

  return (
    <div className="collaborators-role-list">
      <h2 className="collaborators-role-list__title">{t('collaborators.title')}</h2>
      <p className="collaborators-role-list__lede">{t('collaborators.lede')}</p>

      {isLoading && (
        <div className="collaborators-role-list__loading">
          <Spinner message={t('collaborators.loading')} />
        </div>
      )}

      {error && (
        <p className="collaborators-role-list__error" role="alert">
          {error}
        </p>
      )}

      {actionError && (
        <p className="collaborators-role-list__error" role="alert">
          {actionError}
        </p>
      )}

      {!isLoading && !error && users.length === 0 && (
        <p className="collaborators-role-list__empty">{t('collaborators.empty')}</p>
      )}

      {!isLoading && users.length > 0 && (
        <ul className="collaborators-role-list__list">
          {users.map((user) => {
            const displayName =
              user.memberName || user.nickname || user.email || t('collaborators.collaboratorFallback');
            const editable = canChangeUserRole(actorRole, user.role, actorUserId, user.userId, plan);
            const options = getAssignableRoles(actorRole);

            return (
              <li key={user.userId} className="collaborators-role-list__item">
                <div className="collaborators-role-list__identity">
                  <span className="collaborators-role-list__name">{displayName}</span>
                  {user.nickname && user.memberName && (
                    <span className="collaborators-role-list__meta">{user.nickname}</span>
                  )}
                </div>
                {editable ? (
                  <Select.Root
                    value={user.role}
                    onValueChange={(value) => handleRoleChange(user.userId, value)}
                    disabled={updatingUserId === user.userId}
                  >
                    <Select.Trigger
                      className="form-select-trigger collaborators-role-list__select"
                      aria-label={t('collaborators.roleForAria', { name: displayName })}
                    >
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                        {options.map((role) => (
                          <Select.Item key={role} value={role} className="form-select-item">
                            <Select.ItemText>{getRoleDisplayName(role as UserRole)}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                ) : (
                  <span className="collaborators-role-list__role-static">{getRoleDisplayName(user.role)}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});

export default CollaboratorsRoleList;
